import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  linkWithCredential,
  onAuthStateChanged,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
  confirmPasswordReset as firebaseConfirmPasswordReset,
} from 'firebase/auth';
// Note: createUserWithEmailAndPassword and updateProfile remain in FirebaseAuthGateway interface
// for backward compatibility with test factories, even though register() was removed.
import { auth } from '@/config/firebase';
import { environment } from '@/config/environment';
import {
  AuthRepository,
  AuthResponse,
  LoginCredentials,
} from '../../domain/repositories/AuthRepository';
import { User } from '../../domain/entities/User';

type UserRole = 'superadmin' | 'admin' | 'backupmanager';

function normalizeRole(raw: string | undefined): UserRole {
  switch (raw?.toLowerCase()) {
    case 'superadmin': return 'superadmin';
    case 'admin':      return 'admin';
    case 'backupmanager': return 'backupmanager';
    default:           return 'admin';
  }
}

interface UserApiInfo {
  role: UserRole;
  organizationSlug?: string;
  organizationName?: string;
  organizationId?: string;
  language?: string;
  name?: string;
  surname?: string;
  secondSurname?: string;
  status?: 'active' | 'inactive';
  intercomHash?: string;
  createdAt?: number;
  userId?: string;
}

/**
 * Recupera la información del usuario autenticado desde el backend.
 *
 * Exportado para tests unitarios — el contrato es:
 * - 200: devuelve `UserApiInfo`
 * - 401: lanza `Error('USER_NOT_REGISTERED')`
 * - 403: lanza `Error('USER_INACTIVE')`
 * - cualquier otro `!res.ok`: lanza `Error('auth.errors.generic')`
 * - errores de red de `fetch`: se propagan sin envolver
 */
export async function fetchUserInfoFromApi(_email: string, token: string): Promise<UserApiInfo> {
  const res = await fetch(`${environment.apiUrl}/v2/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('USER_NOT_REGISTERED');
    if (res.status === 403) throw new Error('USER_INACTIVE');
    throw new Error('auth.errors.generic');
  }
  const json = await res.json();
  return {
    role: normalizeRole(json?.role),
    organizationSlug: json?.organizationSlug ?? undefined,
    organizationName: json?.organizationName ?? undefined,
    organizationId: json?.organizationId ?? undefined,
    language: json?.language ?? undefined,
    name: json?.name ?? undefined,
    surname: json?.surname ?? undefined,
    secondSurname: json?.secondSurname ?? undefined,
    status: json?.status === 'inactive' ? 'inactive' : 'active',
    intercomHash: typeof json?.intercomHash === 'string' ? json.intercomHash : undefined,
    createdAt: typeof json?.createdAt === 'number' ? json.createdAt : undefined,
    userId: typeof json?.userId === 'string' ? json.userId : undefined,
  };
}

/**
 * Aplica el idioma preferido del usuario y lo persiste si no estaba guardado.
 * Si el usuario tiene idioma en Firestore → lo aplica.
 * Si no → detecta del navegador, aplica y persiste.
 */
async function applyAndPersistLanguage(language: string | undefined, token: string): Promise<void> {
  try {
    // Importación dinámica para evitar dependencia circular y facilitar tests
    const { default: i18n } = await import('@/shared/infrastructure/i18n/config');
    const targetLang = language ?? detectBrowserLanguage();
    await i18n.changeLanguage(targetLang);

    if (!language) {
      // Persistir en Firestore para próximas sesiones (fire-and-forget)
      fetch(`${environment.apiUrl}/v2/auth/me`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: targetLang }),
      }).catch(() => {});
    }
  } catch {
    // No bloquear el login si falla el idioma
  }
}

function detectBrowserLanguage(): string {
  const lang = navigator.language?.toLowerCase() ?? 'es';
  if (lang.startsWith('en')) return 'en';
  return 'es';
}

/**
 * Puerto interno con las funciones de `firebase/auth` que el repositorio invoca.
 *
 * Por qué existe:
 * - El SDK modular de Firebase v9 no se puede mockear con `vi.mock('firebase/auth')`
 *   de forma fiable, ni con `vi.spyOn` sobre el namespace (los bindings ESM son
 *   no-configurables).
 * - En lugar de eso inyectamos las funciones via constructor. En producción el
 *   constructor usa las funciones reales; en tests se pasan stubs.
 *
 * Mantenerlo `internal` (no exportado por el barrel) — solo lo usa el repositorio
 * y sus tests directos.
 */
export interface FirebaseAuthGateway {
  signInWithEmailAndPassword: typeof signInWithEmailAndPassword;
  signInWithPopup: typeof signInWithPopup;
  signOut: typeof signOut;
  createUserWithEmailAndPassword: typeof createUserWithEmailAndPassword;
  signInWithCustomToken: typeof signInWithCustomToken;
  linkWithCredential: typeof linkWithCredential;
  updateProfile: typeof updateProfile;
  verifyPasswordResetCode: typeof firebaseVerifyPasswordResetCode;
  confirmPasswordReset: typeof firebaseConfirmPasswordReset;
  onAuthStateChanged: typeof onAuthStateChanged;
}

const defaultFirebaseGateway: FirebaseAuthGateway = {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  linkWithCredential,
  updateProfile,
  verifyPasswordResetCode: firebaseVerifyPasswordResetCode,
  confirmPasswordReset: firebaseConfirmPasswordReset,
  onAuthStateChanged,
};

/**
 * AuthHttpRepository (Adapter)
 * Implementación concreta del AuthRepository usando Firebase Auth
 */
export class AuthFirebaseRepository implements AuthRepository {
  private readonly fb: FirebaseAuthGateway;

  constructor(gateway: FirebaseAuthGateway = defaultFirebaseGateway) {
    this.fb = gateway;
  }

  /**
   * Login con email y contraseña
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    let firebaseUser;
    let token: string;
    let email: string;
    try {
      // LLamar a Firebase
      const userCredential = await this.fb.signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      firebaseUser = userCredential.user;
      token = await firebaseUser.getIdToken();
      email = firebaseUser.email || credentials.email;
    } catch (error: any) {
      throw this.mapFirebaseError(error);
    }

    // fetchUserInfoFromApi gestiona sus errores y los propaga sin envolver
    let userInfo;
    try {
      userInfo = await fetchUserInfoFromApi(email, token);
    } catch (error) {
      await this.fb.signOut(auth);
      throw error;
    }

    await applyAndPersistLanguage(userInfo.language, token);

    const user = User.fromPrimitives({
      id: firebaseUser.uid,
      email,
      role: userInfo.role,
      name: userInfo.name ?? undefined,
      photoUrl: firebaseUser.photoURL || undefined,
      organizationSlug: userInfo.organizationSlug,
      organizationName: userInfo.organizationName,
      surname: userInfo.surname,
      secondSurname: userInfo.secondSurname,
      status: userInfo.status,
      intercomHash: userInfo.intercomHash,
      createdAt: userInfo.createdAt,
      userId: userInfo.userId,
    });

    return { user, token };
  }

  /**
   * Login con Google
   */
  async loginWithGoogle(): Promise<AuthResponse> {
    let firebaseUser;
    let token: string;
    let email: string;
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await this.fb.signInWithPopup(auth, provider);

      firebaseUser = userCredential.user;
      token = await firebaseUser.getIdToken();
      email = firebaseUser.email || '';
    } catch (error: any) {
      throw this.mapFirebaseError(error);
    }

    let userInfo;
    try {
      userInfo = await fetchUserInfoFromApi(email, token);
    } catch (error) {
      await this.fb.signOut(auth);
      throw error;
    }

    await applyAndPersistLanguage(userInfo.language, token);

    const user = User.fromPrimitives({
      id: firebaseUser.uid,
      email,
      role: userInfo.role,
      name: userInfo.name ?? undefined,
      photoUrl: firebaseUser.photoURL || undefined,
      organizationSlug: userInfo.organizationSlug,
      organizationName: userInfo.organizationName,
      surname: userInfo.surname,
      secondSurname: userInfo.secondSurname,
      status: userInfo.status,
      intercomHash: userInfo.intercomHash,
      createdAt: userInfo.createdAt,
      userId: userInfo.userId,
    });

    return { user, token };
  }

  /**
   * Login con Microsoft
   */
  async loginWithMicrosoft(): Promise<AuthResponse> {
    let firebaseUser;
    let token: string;
    let email: string;
    try {
      const provider = new OAuthProvider('microsoft.com');
      let userCredential;

      try {
        userCredential = await this.fb.signInWithPopup(auth, provider);
      } catch (error: any) {
        if (
          error?.code === 'auth/account-exists-with-different-credential' ||
          error?.code === 'auth/credential-already-in-use'
        ) {
          const pendingCredential = OAuthProvider.credentialFromError(error);
          const emailFromError: string = error?.customData?.email ?? '';
          if (!pendingCredential || !emailFromError) throw error;

          userCredential = await this.autoLinkMicrosoft(emailFromError, pendingCredential);
        } else {
          throw error;
        }
      }

      firebaseUser = userCredential.user;
      token = await firebaseUser.getIdToken();
      email = firebaseUser.email || '';
    } catch (error: any) {
      throw this.mapFirebaseError(error);
    }

    let userInfo;
    try {
      userInfo = await fetchUserInfoFromApi(email, token);
    } catch (error) {
      await this.fb.signOut(auth);
      throw error;
    }

    await applyAndPersistLanguage(userInfo.language, token);

    const user = User.fromPrimitives({
      id: firebaseUser.uid,
      email,
      role: userInfo.role,
      name: userInfo.name ?? undefined,
      photoUrl: firebaseUser.photoURL || undefined,
      organizationSlug: userInfo.organizationSlug,
      organizationName: userInfo.organizationName,
      surname: userInfo.surname,
      secondSurname: userInfo.secondSurname,
      status: userInfo.status,
      intercomHash: userInfo.intercomHash,
      createdAt: userInfo.createdAt,
      userId: userInfo.userId,
    });

    return { user, token };
  }

  /**
   * Vincula Microsoft automáticamente: pide un custom token al backend,
   * inicia sesión con él y vincula la credential pendiente.
   */
  private async autoLinkMicrosoft(
    email: string,
    pendingCredential: NonNullable<ReturnType<typeof OAuthProvider.credentialFromError>>
  ) {
    const res = await fetch(`${environment.apiUrl}/v2/public/account/link-microsoft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!res.ok) {
      throw new Error('Could not link Microsoft account');
    }

    // El backend v2 responde SIEMPRE 200 en este endpoint (D7, cierre DT-050) —
    // `customToken` solo viene presente si el usuario existe y tiene firebaseUid.
    // Su ausencia es la nueva señal de fallo de vinculación (antes era un 404).
    const { customToken } = await res.json();
    if (!customToken) {
      throw new Error('Could not link Microsoft account');
    }

    const customCredential = await this.fb.signInWithCustomToken(auth, customToken);
    await this.fb.linkWithCredential(customCredential.user, pendingCredential);
    return customCredential;
  }

  /**
   * Cerrar sesión
   */
  async logout(): Promise<void> {
    await this.fb.signOut(auth);
  }

  /**
   * Verificar si el token es válido
   * En Firebase, si hay un usuario autenticado, el token es válido
   */
  async verifyToken(_token: string): Promise<boolean> {
    return auth.currentUser !== null;
  }

  /**
   * Obtener token fresco del usuario actual
   * Útil para el interceptor de Axios
   */
  async getCurrentToken(): Promise<string | null> {
    // Camino rápido: Firebase ya restauró la sesión (caso normal en rutas con ProtectedRoute).
    if (auth.currentUser) return auth.currentUser.getIdToken();

    // Carrera de rehidratación: en rutas públicas-pero-autenticadas (p.ej. /pricing accedida
    // por URL directa o refresh) la app monta y pide el token ANTES de que Firebase restaure
    // la sesión persistida, así que `currentUser` es null durante unos ms. Devolver null aquí
    // mandaría la petición sin Authorization → 401 → logout en cascada. Esperamos UNA emisión
    // de onAuthStateChanged: dispara con el usuario restaurado o con null si es anónimo real.
    return new Promise<string | null>((resolve) => {
      const unsubscribe = this.fb.onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user ? user.getIdToken() : null);
      });
    });
  }

  /**
   * Solicitar reset de contraseña mediante backend propio (no Firebase)
   */
  async resetPassword(email: string): Promise<void> {
    const res = await fetch(`${environment.apiUrl}/v2/public/account/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, clientUrl: window.location.origin }),
    });
    if (!res.ok) {
      throw new Error('auth.errors.generic');
    }
  }

  /**
   * Establecer contraseña mediante token del backend propio
   */
  async setPassword(token: string, password: string): Promise<void> {
    const res = await fetch(`${environment.apiUrl}/v2/public/account/set-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? 'auth.errors.generic');
    }
  }

  /**
   * Verificar que el código de reset de contraseña es válido y obtener el email asociado
   */
  async verifyPasswordResetCode(oobCode: string): Promise<string> {
    try {
      return await this.fb.verifyPasswordResetCode(auth, oobCode);
    } catch (error: any) {
      throw this.mapFirebaseError(error);
    }
  }

  /**
   * Confirmar el reset de contraseña con el nuevo password
   */
  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    try {
      await this.fb.confirmPasswordReset(auth, oobCode, newPassword);
    } catch (error: any) {
      throw this.mapFirebaseError(error);
    }
  }

  /**
   * Enviar Magic Link de acceso sin contraseña.
   *
   * Llama al backend propio de Klonup. El backend responde SIEMPRE 200 por
   * privacidad (no revela si el email está registrado), así que solo lanzamos
   * ante un error de red o un status inesperado.
   */
  async sendMagicLink(email: string): Promise<void> {
    const res = await fetch(`${environment.apiUrl}/v2/public/magic-link/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        clientUrl: window.location.origin,
        language: detectBrowserLanguage(),
      }),
    });
    if (!res.ok) {
      throw new Error('auth.errors.generic');
    }
  }

  /**
   * Canjear un Magic Link recibido por email.
   * - Canjea el token contra POST /v2/public/magic-link/redeem → recibe un customToken
   * - Inicia sesión con signInWithCustomToken
   * - Valida la identidad en /v2/auth/me (si falla → signOut + re-throw)
   * - Aplica idioma, construye User y devuelve { user, token }
   *
   * El token de la URL identifica al usuario — no hace falta el email ni localStorage.
   */
  async redeemMagicLink(token: string): Promise<AuthResponse> {
    let customToken: string;
    const res = await fetch(`${environment.apiUrl}/v2/public/magic-link/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? 'auth.errors.invalid_action_code');
    }
    customToken = (await res.json()).customToken;

    let firebaseUser;
    let idToken: string;
    let email: string;
    try {
      const userCredential = await this.fb.signInWithCustomToken(auth, customToken);
      firebaseUser = userCredential.user;
      idToken = await firebaseUser.getIdToken();
      email = firebaseUser.email || '';
    } catch (error: any) {
      throw this.mapFirebaseError(error);
    }

    let userInfo;
    try {
      userInfo = await fetchUserInfoFromApi(email, idToken);
    } catch (error) {
      await this.fb.signOut(auth);
      throw error;
    }

    await applyAndPersistLanguage(userInfo.language, idToken);

    const user = User.fromPrimitives({
      id: firebaseUser.uid,
      email,
      role: userInfo.role,
      name: userInfo.name ?? undefined,
      photoUrl: firebaseUser.photoURL || undefined,
      organizationSlug: userInfo.organizationSlug,
      organizationName: userInfo.organizationName,
      surname: userInfo.surname,
      secondSurname: userInfo.secondSurname,
      status: userInfo.status,
      intercomHash: userInfo.intercomHash,
      createdAt: userInfo.createdAt,
      userId: userInfo.userId,
    });

    return { user, token: idToken };
  }

  /**
   * Mapea errores de Firebase a mensajes amigables
   */
  private mapFirebaseError(error: any): Error {
    const errorCode = error.code;

    const errorMessages: Record<string, string> = {
      // Login errors
      'auth/invalid-email': 'El email no es válido',
      'auth/user-disabled': 'Esta cuenta ha sido deshabilitada',
      'auth/user-not-found': 'No existe una cuenta con este email',
      'auth/wrong-password': 'Contraseña incorrecta',
      'auth/invalid-credential': 'Email o contraseña incorrectos',
      'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde',
      'auth/popup-closed-by-user': 'Se cerró la ventana de autenticación',
      'auth/cancelled-popup-request': 'Operación cancelada',
      'auth/network-request-failed': 'Error de conexión. Verifica tu internet',
      // Register errors
      'auth/email-already-in-use': 'Ya existe una cuenta con este email',
      'auth/weak-password': 'La contraseña es demasiado débil. Usa al menos 6 caracteres',
      'auth/operation-not-allowed': 'El registro con email/contraseña no está habilitado',
      // Reset password errors
      'auth/expired-action-code': 'El enlace ha expirado. Solicita uno nuevo.',
      'auth/invalid-action-code': 'El enlace no es válido o ya fue usado.',
      // Magic link errors
      'auth/missing-email': 'Introduce un correo válido',
    };

    const message = errorMessages[errorCode] || 'Ha ocurrido un error. Intenta de nuevo';
    const mapped = new Error(message);
    (mapped as any).code = errorCode;
    return mapped;
  }
}
